/**
 * Pure geometry + bookkeeping for the Sponsor Ad Style Editor.
 * No React, no DOM — tested by frontend/scripts/adEditorGeometry.test.mjs.
 *
 * Every rectangle is {x, y, w, h} in PERCENT of the artboard (0-100),
 * the same units the layout is saved in and the server renders from.
 * The artboard is CANVAS_W x CANVAS_H pixels, so 1% is 13px across but
 * 8px down — anything that has to look right on screen (keeping a
 * shape, snapping to a pixel grid, nudging by a pixel) converts through
 * those two numbers.
 */

export const CANVAS_W = 1300;
export const CANVAS_H = 800;

export const MIN_W = 1;      // percent
export const MIN_H = 1;      // percent

export const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

const round2 = (n) => Math.round(n * 100) / 100;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const pxToPctX = (px) => (px / CANVAS_W) * 100;
export const pxToPctY = (px) => (px / CANVAS_H) * 100;
export const pctToPxX = (pct) => (pct / 100) * CANVAS_W;
export const pctToPxY = (pct) => (pct / 100) * CANVAS_H;

// ---------------------------------------------------------------- kinds

/**
 * What an element IS, from its key. Mirrors `_sanitize_element` in
 * backend/routes/ads.py — the image variant of the sponsor bar is
 * tested before the plain bar so its longer prefix isn't swallowed.
 */
export function elementKind(key) {
  const k = String(key || "");
  if (k === "logo") return "logo";
  if (k === "sponsor_bar_image" || k.startsWith("sponsor_bar_image_")) return "barImage";
  if (k === "image" || k.startsWith("image_")) return "image";
  if (k === "sponsor_bar" || k.startsWith("sponsor_bar_")) return "bar";
  if (k === "text" || k.startsWith("text_")) return "customText";
  return "text";
}

export const isImageKind = (key) => {
  const kind = elementKind(key);
  return kind === "image" || kind === "barImage";
};

/** Elements that can exist more than once (and so can be duplicated). */
export const canDuplicate = (key) => {
  const kind = elementKind(key);
  return kind === "image" || kind === "barImage" || kind === "bar" || kind === "customText";
};

/** The key family a duplicate belongs to: "image_3" -> "image". */
export function keyFamily(key) {
  switch (elementKind(key)) {
    case "barImage": return "sponsor_bar_image";
    case "image": return "image";
    case "bar": return "sponsor_bar";
    case "customText": return "text";
    default: return String(key || "");
  }
}

/**
 * First free key in a family: "image", then "image_2", "image_3"…
 * Never reuses a key that is still in `elements`, which the old
 * count-based naming did after an element had been removed.
 */
export function uniqueKey(elements, family) {
  const taken = new Set((elements || []).map((e) => e.key));
  if (!taken.has(family)) return family;
  let n = 2;
  while (taken.has(`${family}_${n}`)) n += 1;
  return `${family}_${n}`;
}

/** Whether an element keeps its shape while resizing, unless told otherwise. */
export function keepsShape(el) {
  if (!el) return false;
  if (typeof el.lock_ratio === "boolean") return el.lock_ratio;
  const kind = elementKind(el.key);
  return kind === "image" || kind === "logo";
}

// ----------------------------------------------------------------- rects

export const rectOf = (el) => ({
  x: Number(el.x) || 0, y: Number(el.y) || 0,
  w: Number(el.w) || 0, h: Number(el.h) || 0,
});

/** Normalise a drag from (x0,y0) to (x1,y1) into a positive rect. */
export function rectFromPoints(x0, y0, x1, y1) {
  return {
    x: Math.min(x0, x1), y: Math.min(y0, y1),
    w: Math.abs(x1 - x0), h: Math.abs(y1 - y0),
  };
}

export function rectsIntersect(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/** Bounding box of several rects, or null for none. */
export function boundsOf(rects) {
  const list = (rects || []).filter(Boolean);
  if (!list.length) return null;
  const x0 = Math.min(...list.map((r) => r.x));
  const y0 = Math.min(...list.map((r) => r.y));
  const x1 = Math.max(...list.map((r) => r.x + r.w));
  const y1 = Math.max(...list.map((r) => r.y + r.h));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Keys of the elements a selection rectangle touches, in layer order.
 * A zero-size rectangle (a plain click on empty artboard) selects
 * nothing.
 */
export function marqueeHits(elements, rect) {
  if (!rect || rect.w <= 0 || rect.h <= 0) return [];
  return (elements || [])
    .filter((el) => rectsIntersect(rectOf(el), rect))
    .map((el) => el.key);
}

// ------------------------------------------------------------------ move

/**
 * Move a group by (dx, dy) percent, keeping the whole group on the
 * artboard. `starts` is {key: rect}. Returns {key: {x, y}}.
 */
export function moveRects(starts, dx, dy) {
  const keys = Object.keys(starts || {});
  const box = boundsOf(keys.map((k) => starts[k]));
  if (!box) return {};
  const cdx = clamp(dx, -box.x, 100 - (box.x + box.w));
  const cdy = clamp(dy, -box.y, 100 - (box.y + box.h));
  const out = {};
  for (const k of keys) {
    out[k] = { x: round2(starts[k].x + cdx), y: round2(starts[k].y + cdy) };
  }
  return out;
}

/** Snap a percent value to the nearest `gridPx` pixels on its axis. */
export function snapToGridPct(value, axis, gridPx = 50) {
  const size = axis === "y" ? CANVAS_H : CANVAS_W;
  return (Math.round(((value / 100) * size) / gridPx) * gridPx) / size * 100;
}

/**
 * Smart guides for a move. `box` is where the dragged group would land;
 * `others` are the rects of everything that is NOT being dragged.
 * Left / centre / right (and top / middle / bottom) of the group are
 * compared with the artboard's edges and centre and with every other
 * element's edges and centre. The closest match inside the tolerance
 * wins on each axis.
 *
 * Returns {dx, dy, guideX, guideY}: the extra shift to apply, and the
 * percent position of the guide line to draw (null when nothing
 * snapped on that axis).
 */
export function snapMove(box, others, tolX = 0.5, tolY = 0.8) {
  const targetsX = [0, 50, 100];
  const targetsY = [0, 50, 100];
  for (const o of others || []) {
    targetsX.push(o.x, o.x + o.w / 2, o.x + o.w);
    targetsY.push(o.y, o.y + o.h / 2, o.y + o.h);
  }
  const pick = (points, targets, tol) => {
    let best = null;
    for (const p of points) {
      for (const t of targets) {
        const d = t - p;
        if (Math.abs(d) <= tol && (best === null || Math.abs(d) < Math.abs(best.d))) {
          best = { d, t };
        }
      }
    }
    return best;
  };
  const bx = pick([box.x, box.x + box.w / 2, box.x + box.w], targetsX, tolX);
  const by = pick([box.y, box.y + box.h / 2, box.y + box.h], targetsY, tolY);
  return {
    dx: bx ? bx.d : 0, dy: by ? by.d : 0,
    guideX: bx ? bx.t : null, guideY: by ? by.t : null,
  };
}

// ---------------------------------------------------------------- resize

/**
 * Resize `start` by dragging `handle` ("nw", "n", "ne", "e", "se", "s",
 * "sw", "w") by (dx, dy) percent.
 *
 * - The opposite edge / corner stays put.
 * - `keepRatio` keeps the shape the element had when the drag began.
 *   On a corner the larger pull wins; on an edge the other dimension
 *   follows and stays centred on its own axis.
 * - The result never leaves the artboard and never gets smaller than
 *   MIN_W x MIN_H. With `keepRatio`, hitting an artboard edge stops
 *   BOTH dimensions, so the shape is kept right up to the edge.
 */
export function resizeRect(start, handle, dx, dy, opts = {}) {
  const keepRatio = !!opts.keepRatio;
  const minW = opts.minW ?? MIN_W;
  const minH = opts.minH ?? MIN_H;
  const west = handle.includes("w");
  const east = handle.includes("e");
  const north = handle.includes("n");
  const south = handle.includes("s");

  let left = start.x;
  let top = start.y;
  let right = start.x + start.w;
  let bottom = start.y + start.h;

  if (!keepRatio) {
    if (west) left = clamp(start.x + dx, 0, right - minW);
    if (east) right = clamp(right + dx, left + minW, 100);
    if (north) top = clamp(start.y + dy, 0, bottom - minH);
    if (south) bottom = clamp(bottom + dy, top + minH, 100);
    return {
      x: round2(left), y: round2(top),
      w: round2(right - left), h: round2(bottom - top),
    };
  }

  // Shape in screen pixels: width / height.
  const ratio = (pctToPxX(start.w) || 1) / (pctToPxY(start.h) || 1);
  // h% that goes with a given w%, and back.
  const hFor = (w) => pxToPctY(pctToPxX(w) / ratio);
  const wFor = (h) => pxToPctX(pctToPxY(h) * ratio);

  const horizontal = west || east;
  const vertical = north || south;
  let w = start.w;
  if (horizontal && vertical) {
    const wFromX = start.w + (east ? dx : -dx);
    const wFromY = wFor(start.h + (south ? dy : -dy));
    // Follow whichever pull changes the size more.
    w = Math.abs(wFromX - start.w) >= Math.abs(wFromY - start.w) ? wFromX : wFromY;
  } else if (horizontal) {
    w = start.w + (east ? dx : -dx);
  } else {
    w = wFor(start.h + (south ? dy : -dy));
  }

  // Room available in each direction from the fixed edge / centre line.
  const cx = start.x + start.w / 2;
  const cy = start.y + start.h / 2;
  const maxW = horizontal
    ? (east ? 100 - start.x : start.x + start.w)
    : 2 * Math.min(cx, 100 - cx);
  const maxH = vertical
    ? (south ? 100 - start.y : start.y + start.h)
    : 2 * Math.min(cy, 100 - cy);

  w = Math.min(w, maxW, wFor(maxH));
  w = Math.max(w, minW, wFor(minH));
  // The floor can overshoot the ceiling on a sliver of an element
  // squeezed against an edge; the ceiling (staying on the artboard) wins.
  w = Math.min(w, maxW, wFor(maxH));
  const h = hFor(w);

  if (horizontal) {
    if (east) right = left + w; else left = right - w;
  } else {
    left = cx - w / 2;
  }
  if (vertical) {
    if (south) bottom = top + h; else top = bottom - h;
  } else {
    top = cy - h / 2;
  }
  return { x: round2(Math.max(0, left)), y: round2(Math.max(0, top)), w: round2(w), h: round2(h) };
}

/**
 * After a free (not keep-shape) resize with "Snap 50px" on: pull the
 * edges that `handle` moves onto the 50px grid. The edges it does not
 * move stay exactly where they were.
 */
export function snapResizeToGrid(rect, handle, gridPx = 50) {
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.w;
  let bottom = rect.y + rect.h;
  if (handle.includes("w")) left = clamp(snapToGridPct(left, "x", gridPx), 0, right - MIN_W);
  if (handle.includes("e")) right = clamp(snapToGridPct(right, "x", gridPx), left + MIN_W, 100);
  if (handle.includes("n")) top = clamp(snapToGridPct(top, "y", gridPx), 0, bottom - MIN_H);
  if (handle.includes("s")) bottom = clamp(snapToGridPct(bottom, "y", gridPx), top + MIN_H, 100);
  return {
    x: round2(left), y: round2(top),
    w: round2(right - left), h: round2(bottom - top),
  };
}

/**
 * Box that shows a `naturalW` x `naturalH` picture undistorted, keeping
 * the element's current width and centre. Shrinks to stay on the
 * artboard.
 */
export function fitToAspect(rect, naturalW, naturalH) {
  if (!(naturalW > 0) || !(naturalH > 0)) return { ...rect };
  const ratio = naturalW / naturalH;
  let w = rect.w;
  let h = pxToPctY(pctToPxX(w) / ratio);
  if (h > 100) {
    h = 100;
    w = pxToPctX(pctToPxY(h) * ratio);
  }
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const x = clamp(cx - w / 2, 0, 100 - w);
  const y = clamp(cy - h / 2, 0, 100 - h);
  return { x: round2(x), y: round2(y), w: round2(w), h: round2(h) };
}

// --------------------------------------------------------- align / space

export const ALIGN_MODES = ["left", "hcenter", "right", "top", "vcenter", "bottom"];

/**
 * Align the elements in `keys`. One element aligns to the artboard;
 * two or more align to the box around the selection. Returns a new
 * elements array (untouched elements keep their identity).
 */
export function alignElements(elements, keys, mode) {
  const set = new Set(keys || []);
  const picked = (elements || []).filter((e) => set.has(e.key));
  if (!picked.length) return elements;
  const ref = picked.length === 1
    ? { x: 0, y: 0, w: 100, h: 100 }
    : boundsOf(picked.map(rectOf));
  return elements.map((el) => {
    if (!set.has(el.key)) return el;
    const r = rectOf(el);
    let { x, y } = r;
    if (mode === "left") x = ref.x;
    else if (mode === "hcenter") x = ref.x + (ref.w - r.w) / 2;
    else if (mode === "right") x = ref.x + ref.w - r.w;
    else if (mode === "top") y = ref.y;
    else if (mode === "vcenter") y = ref.y + (ref.h - r.h) / 2;
    else if (mode === "bottom") y = ref.y + ref.h - r.h;
    return { ...el, x: round2(clamp(x, 0, 100 - r.w)), y: round2(clamp(y, 0, 100 - r.h)) };
  });
}

/**
 * Space three or more elements evenly along `axis` ("x" or "y"): the
 * two outermost stay where they are and the gaps between the rest are
 * made equal. Fewer than three is a no-op.
 */
export function distributeElements(elements, keys, axis) {
  const set = new Set(keys || []);
  const picked = (elements || []).filter((e) => set.has(e.key));
  if (picked.length < 3) return elements;
  const pos = axis === "y" ? "y" : "x";
  const size = axis === "y" ? "h" : "w";
  const sorted = [...picked].sort((a, b) => rectOf(a)[pos] - rectOf(b)[pos]);
  const first = rectOf(sorted[0]);
  const last = rectOf(sorted[sorted.length - 1]);
  const span = last[pos] + last[size] - first[pos];
  const used = sorted.reduce((sum, e) => sum + rectOf(e)[size], 0);
  const gap = (span - used) / (sorted.length - 1);
  const next = {};
  let cursor = first[pos];
  for (const el of sorted) {
    next[el.key] = round2(cursor);
    cursor += rectOf(el)[size] + gap;
  }
  return elements.map((el) => (set.has(el.key) ? { ...el, [pos]: next[el.key] } : el));
}

/** Move the selection by whole pixels, staying on the artboard. */
export function nudgeElements(elements, keys, dxPx, dyPx) {
  const set = new Set(keys || []);
  const starts = {};
  for (const el of elements || []) {
    if (set.has(el.key)) starts[el.key] = rectOf(el);
  }
  const moved = moveRects(starts, pxToPctX(dxPx), pxToPctY(dyPx));
  return (elements || []).map((el) => (moved[el.key] ? { ...el, ...moved[el.key] } : el));
}

// ------------------------------------------------------------ list edits

export function removeElements(elements, keys) {
  const set = new Set(keys || []);
  return (elements || []).filter((el) => !set.has(el.key));
}

/**
 * Add copies of `sources` to `elements`, each with a fresh key and
 * nudged down-right by `offsetPx` so it doesn't hide its original.
 * Elements that can only exist once (logo, title, phone…) are skipped.
 * Returns {elements, added: [keys], skipped: [keys]}.
 */
export function addCopies(elements, sources, offsetPx = 20) {
  const out = [...(elements || [])];
  const added = [];
  const skipped = [];
  for (const src of sources || []) {
    if (!canDuplicate(src.key)) {
      skipped.push(src.key);
      continue;
    }
    const r = rectOf(src);
    const copy = {
      ...JSON.parse(JSON.stringify(src)),
      key: uniqueKey(out, keyFamily(src.key)),
      x: round2(clamp(r.x + pxToPctX(offsetPx), 0, Math.max(0, 100 - r.w))),
      y: round2(clamp(r.y + pxToPctY(offsetPx), 0, Math.max(0, 100 - r.h))),
    };
    out.push(copy);
    added.push(copy.key);
  }
  return { elements: out, added, skipped };
}

/**
 * Change the stacking order of the selection. The elements array is
 * drawn first-to-last, so the LAST element is on top.
 *   "front" / "back"       — all the way
 *   "forward" / "backward" — one step
 * The selection keeps its own internal order.
 */
export function reorderElements(elements, keys, direction) {
  const list = [...(elements || [])];
  const set = new Set(keys || []);
  if (!list.some((e) => set.has(e.key))) return elements;
  if (direction === "front" || direction === "back") {
    const picked = list.filter((e) => set.has(e.key));
    const rest = list.filter((e) => !set.has(e.key));
    return direction === "front" ? [...rest, ...picked] : [...picked, ...rest];
  }
  if (direction === "forward") {
    for (let i = list.length - 2; i >= 0; i -= 1) {
      if (set.has(list[i].key) && !set.has(list[i + 1].key)) {
        [list[i], list[i + 1]] = [list[i + 1], list[i]];
      }
    }
    return list;
  }
  if (direction === "backward") {
    for (let i = 1; i < list.length; i += 1) {
      if (set.has(list[i].key) && !set.has(list[i - 1].key)) {
        [list[i], list[i - 1]] = [list[i - 1], list[i]];
      }
    }
    return list;
  }
  return elements;
}

// --------------------------------------------------------------- history

export const HISTORY_LIMIT = 100;

export const emptyHistory = () => ({ past: [], future: [], tag: null, at: 0 });

/**
 * Record `before` (the layout as it was) ahead of a change.
 * Changes that share a `tag` and arrive within `windowMs` of each
 * other — dragging a slider, holding an arrow key — collapse into ONE
 * undo step instead of one per tick.
 */
export function pushHistory(history, before, tag = null, now = Date.now(), windowMs = 700) {
  const h = history || emptyHistory();
  if (tag && h.tag === tag && now - h.at < windowMs && h.past.length) {
    return { ...h, future: [], at: now };
  }
  const past = [...h.past, before];
  if (past.length > HISTORY_LIMIT) past.shift();
  return { past, future: [], tag, at: now };
}

/** Returns {history, layout} or null when there is nothing to undo. */
export function undoHistory(history, current) {
  const h = history || emptyHistory();
  if (!h.past.length) return null;
  const past = h.past.slice(0, -1);
  return {
    history: { past, future: [current, ...h.future], tag: null, at: 0 },
    layout: h.past[h.past.length - 1],
  };
}

/** Returns {history, layout} or null when there is nothing to redo. */
export function redoHistory(history, current) {
  const h = history || emptyHistory();
  if (!h.future.length) return null;
  const [next, ...future] = h.future;
  return {
    history: { past: [...h.past, current], future, tag: null, at: 0 },
    layout: next,
  };
}

// ----------------------------------------------------------------- banner

/**
 * The element a 1300x200 banner becomes on the artboard: full width,
 * 200px tall, sitting on the bottom edge. `stretch` is exact here —
 * the box has the banner's own shape.
 */
export function bannerElement(elements, src) {
  const h = pxToPctY(200);
  return {
    key: uniqueKey(elements, "sponsor_bar_image"),
    x: 0, y: round2(100 - h), w: 100, h: round2(h),
    src, fit: "stretch", lock_ratio: true,
  };
}
